import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { api, ifMatch, newIdempotencyKey, unwrap } from '@/api/client';
import { useCommand } from '@/app/commands';
import { orgKey } from '@/app/queryClient';
import { useOrg } from '@/app/session';
import { Button, Checkbox, DiffView, EmptyState, Field, Menu, Modal, Section, Select, StatusDot, TextArea, TextInput, Time } from '@/components/ui';
import { DataTable } from '@/components/data';
import { ConfirmDialog, FormErrorSummary, ReadOnlyNotice, ScopeList, fieldErrors, usePagedList, type Schemas } from './shared';

type Actor = Schemas['Actor'];
type Scope = Schemas['Scope'];
const KIND_LABEL: Record<Schemas['MachineActorKind'], string> = { agent: 'Agent', connector: 'Mail sync', runner: 'Runner', migration: 'Import' };

export function ActorsPage() {
  const { orgId, isAdmin } = useOrg();
  const key = orgKey(orgId, 'actors');
  const { pager, query } = usePagedList<Actor>(key, (q, signal) =>
    unwrap(api.GET('/api/v1/orgs/{orgId}/actors', { params: { path: { orgId }, query: q }, signal })),
  );
  const [edit, setEdit] = useState<{ actor?: Actor } | null>(null);
  const [disableTarget, setDisableTarget] = useState<Actor | null>(null);
  const disable = useCommand({
    run: (a: Actor) =>
      unwrap(
        api.DELETE('/api/v1/orgs/{orgId}/actors/{actorId}', {
          params: { path: { orgId, actorId: a.id }, header: { 'If-Match': ifMatch(a.version), 'Idempotency-Key': newIdempotencyKey() } },
        }),
      ),
    success: 'Account disabled. Keys revoked, work in progress released.',
    invalidate: [key],
    onSuccess: () => setDisableTarget(null),
  });
  const columns: ColumnDef<Actor, unknown>[] = [
    {
      id: 'name',
      header: 'Account',
      meta: { pinned: true },
      cell: ({ row }) => (
        <span className="settings-cell-name" title={row.original.display_name}>
          {row.original.display_name}
        </span>
      ),
    },
    { id: 'kind', header: 'Kind', cell: ({ row }) => <StatusDot domain="actor" value={row.original.kind} /> },
    {
      id: 'status',
      header: 'State',
      cell: ({ row }) => <StatusDot tone={row.original.status === 'active' ? 'neutral' : 'faint'}>{row.original.status === 'active' ? 'Active' : 'Disabled'}</StatusDot>,
    },
    {
      id: 'purpose',
      header: 'Purpose',
      cell: ({ row }) => (
        <span className="small settings-cell-list" title={row.original.purpose}>
          {row.original.purpose}
        </span>
      ),
    },
    { id: 'scopes', header: 'Scopes', cell: ({ row }) => <ScopeList scopes={row.original.scopes} /> },
    { id: 'updated', header: 'Updated', cell: ({ row }) => <Time value={row.original.updated_at} /> },
    {
      id: 'actions',
      header: '',
      meta: { width: 56 },
      cell: ({ row }) =>
        isAdmin && row.original.kind !== 'human' && row.original.kind !== 'system' ? (
          <Menu
            label={`Actions for ${row.original.display_name}`}
            iconOnly
            icon="more"
            size="sm"
            align="right"
            items={[
              { label: 'Edit', icon: 'edit', onSelect: () => setEdit({ actor: row.original }) },
              { label: 'Disable', icon: 'x', danger: true, disabled: row.original.status !== 'active', onSelect: () => setDisableTarget(row.original) },
            ]}
          />
        ) : null,
    },
  ];
  return (
    <Section
      title="Agent accounts"
      actions={
        isAdmin ? (
          <Button icon="plus" onClick={() => setEdit({})}>
            New account
          </Button>
        ) : undefined
      }
    >
      <p className="small muted">
        Accounts for agents, mail syncs and runners. Keys under API keys above. Work under{' '}
        <Link to="/app/settings/agents/runs">Runs</Link>.
      </p>
      <ReadOnlyNotice canEdit={isAdmin} />
      <DataTable
        caption="Accounts"
        columns={columns}
        getRowId={(a) => a.id}
        query={query}
        pager={pager}
        sort={[]}
        onSortChange={() => undefined}
        // No filters; the toolbar still carries the Columns picker for the column hidden by default.
        toolbar={<></>}
        storageKey="settings.actors"
        defaultHidden={['updated']}
        empty={<EmptyState title="No accounts yet." />}
      />
      {edit && <ActorDialog actor={edit.actor} onClose={() => setEdit(null)} />}
      {disableTarget && (
        <ConfirmDialog
          open
          reason="none"
          danger
          onClose={() => setDisableTarget(null)}
          title="Disable account"
          confirmLabel="Disable account"
          pending={disable.isPending}
          onConfirm={() => disable.mutate(disableTarget)}
          effects={[
            `${disableTarget.display_name} can no longer sign in.`,
            'API keys stop working; work in progress is released.',
            'Recorded work stays attributed.',
          ]}
        />
      )}
    </Section>
  );
}

function ActorDialog({ actor, onClose }: { actor?: Actor; onClose: () => void }) {
  const { orgId, scopes: delegable, membershipId } = useOrg();
  const [name, setName] = useState(actor?.display_name ?? '');
  const [kind, setKind] = useState<Schemas['MachineActorKind']>('agent');
  const [purpose, setPurpose] = useState(actor?.purpose ?? '');
  const [owner, setOwner] = useState(actor?.owner_membership_id ?? membershipId);
  const [scopes, setScopes] = useState<Set<Scope>>(new Set(actor?.scopes ?? []));
  const [reason, setReason] = useState('');
  const members = useQuery({
    queryKey: orgKey(orgId, 'memberships', 'owners'),
    queryFn: ({ signal }) => unwrap(api.GET('/api/v1/orgs/{orgId}/memberships', { params: { path: { orgId }, query: { status: 'active', limit: 100 } }, signal })),
  });
  const save = useCommand({
    run: () => {
      const header = { 'Idempotency-Key': newIdempotencyKey() };
      const body = { display_name: name.trim(), purpose: purpose.trim(), owner_membership_id: owner, scopes: [...scopes], reason: reason.trim() || undefined };
      if (actor) {
        return unwrap(
          api.PATCH('/api/v1/orgs/{orgId}/actors/{actorId}', {
            params: { path: { orgId, actorId: actor.id }, header: { ...header, 'If-Match': ifMatch(actor.version) } },
            body,
          }),
        );
      }
      return unwrap(api.POST('/api/v1/orgs/{orgId}/actors', { params: { path: { orgId }, header }, body: { ...body, kind } }));
    },
    success: actor ? 'Account saved.' : 'Account created. Create a key under API keys.',
    invalidate: [orgKey(orgId, 'actors')],
    onSuccess: onClose,
  });
  const errs = fieldErrors(save.error);
  // Existing scopes outside your grants stay visible but cannot be added by you.
  const shown = [...new Set<string>([...delegable, ...(actor?.scopes ?? [])])].sort();
  const toggle = (s: Scope) =>
    setScopes((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={actor ? 'Edit account' : 'New account'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={save.isPending} disabled={!name.trim() || !purpose.trim() || !owner} onClick={() => save.mutate(undefined)}>
            Save account
          </Button>
        </>
      }
    >
      <div className="stack">
        <div className="form-grid">
          <Field label="Name" required error={errs.display_name}>
            <TextInput value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          {!actor && (
            <Field label="Kind" error={errs.kind}>
              <Select
                value={kind}
                onChange={(e) => setKind(e.target.value as Schemas['MachineActorKind'])}
                options={(['agent', 'connector', 'runner', 'migration'] as const).map((k) => ({ value: k, label: KIND_LABEL[k] }))}
              />
            </Field>
          )}
          <Field label="Owner" required error={errs.owner_membership_id}>
            <Select
              value={owner}
              onChange={(e) => setOwner(e.target.value)}
              options={(members.data?.items ?? []).map((m) => ({ value: m.id, label: m.display_name || m.username }))}
            />
          </Field>
        </div>
        <Field label="Purpose" required error={errs.purpose}>
          <TextArea rows={2} value={purpose} onChange={(e) => setPurpose(e.target.value)} />
        </Field>
        <fieldset className="stack-sm">
          <legend className="field-label">Scopes (only grants you hold can be added)</legend>
          <div className="scope-grid">
            {shown.map((s) => {
              const mine = (delegable as readonly string[]).includes(s);
              return (
                <Checkbox
                  key={s}
                  label={<span className="mono small">{s}</span>}
                  checked={scopes.has(s as Scope)}
                  disabled={!mine && !scopes.has(s as Scope)}
                  onChange={() => toggle(s as Scope)}
                />
              );
            })}
          </div>
          {errs.scopes && <p className="field-error">{errs.scopes}</p>}
        </fieldset>
        {actor && (
          <DiffView
            hideUnchanged
            fields={[
              { name: 'Name', before: actor.display_name, after: name.trim() },
              { name: 'Purpose', before: actor.purpose, after: purpose.trim() },
              { name: 'Owner', before: actor.owner_membership_id, after: owner, mono: true },
              { name: 'Scopes', before: [...actor.scopes].sort(), after: [...scopes].sort() },
            ]}
          />
        )}
        <Field label="Reason" error={errs.reason}>
          <TextArea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        <FormErrorSummary error={save.error} />
      </div>
    </Modal>
  );
}
